import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import DialogContainer from '@shared/ui/dialog/DialogContainer';
import { useAuth } from '@shared/hooks';
import { useCustomFields } from '@features/products/hooks/useCustomFields';
import { useToastStore } from '../../../../application/store/toastStore';
import { container } from '@infrastructure/container';
import {
  DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
  ShopifyProductMatchMapping,
} from '@domain/connection/models/ConnectionSettings';
import { ShopifyMatchPreviewResponse } from '@domain/connection/models/ShopifyMatchPreview';
import ShopifyProductMappingForm from './ShopifyProductMappingForm';
import { APP_CONFIG } from '../../../../config/app.config';

function mappingKey(mapping: ShopifyProductMatchMapping): string {
  return `${mapping.sourceField}|${mapping.targetField}`;
}

const ShopifyConnectionSetup = () => {
  const { id: connectionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useToastStore((state) => state.addToast);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { customFields: customFieldsResponse } = useCustomFields();

  const [preview, setPreview] = useState<ShopifyMatchPreviewResponse | null>(null);
  const [mapping, setMapping] = useState<ShopifyProductMatchMapping>(
    DEFAULT_SHOPIFY_PRODUCT_MATCH_MAPPING,
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const fetchedKeyRef = useRef<string | null>(null);

  const companyCustomFields = customFieldsResponse?.status === 'success'
    ? customFieldsResponse.data
    : [];

  useEffect(() => {
    if (!connectionId || !isAuthenticated) return;

    const requestKey = initialized ? mappingKey(mapping) : '__init__';
    if (fetchedKeyRef.current === requestKey) return;

    setIsLoadingPreview(true);
    setLoadError(null);

    let cancelled = false;
    container.shopifyConnectorRepository
      .getMatchPreview(connectionId, initialized ? mapping : undefined)
      .then((response) => {
        if (cancelled) return;
        if (response.status === 'error') {
          setLoadError(response.error.message || 'Failed to load match preview');
          return;
        }

        const data = response.data;
        fetchedKeyRef.current = mappingKey(data.mapping);
        setPreview(data);
        setMapping(data.mapping);
        setInitialized(true);
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setLoadError(error.message || 'Failed to load match preview');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [connectionId, isAuthenticated, initialized, mapping]);

  const productsFound = preview?.productsFound ?? 0;
  const hasDuplicateValues = preview?.hasDuplicateValues ?? false;
  const canConnect = productsFound > 0 && !hasDuplicateValues;

  const handleConnect = async () => {
    if (!connectionId || !canConnect) return;

    setIsSubmitting(true);
    try {
      const response = await container.shopifyConnectorRepository.completeSetup(
        connectionId,
        mapping,
      );
      if (response.status === 'error') {
        throw new Error(response.error.message);
      }
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      queryClient.invalidateQueries({ queryKey: ['shopify-settings', connectionId] });
      addToast('Import started', 'success');
      navigate('/sales');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to start import', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <DialogContainer maxWidth="640px">
        <Box sx={{ position: 'relative', m: 4 }}>
          <Box>
            <Typography variant="h6" fontWeight="medium" gutterBottom>
              Match products
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              We identify each unique product by linking a Shopify field to a JustStock product field.
            </Typography>

            {isLoadingPreview && !preview && (
              <Stack alignItems="center" spacing={2} sx={{ height: '200px', justifyContent: 'center' }}>
                <CircularProgress />
                <Typography color="text.secondary">Checking product fields…</Typography>
              </Stack>
            )}

            {!isLoadingPreview && loadError && !preview && (
              <Box>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: 'error.light',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'error.main',
                  }}
                >
                  <Typography color="error.main">{loadError}</Typography>
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button variant="outlined" onClick={() => navigate('/company')}>
                    Back to connections
                  </Button>
                </Stack>
              </Box>
            )}

            {preview && (
              <ShopifyProductMappingForm
                mapping={mapping}
                onMappingChange={setMapping}
                fieldCounts={preview.fieldCounts}
                productsScanned={preview.productsScanned}
                matchesFound={preview.matchesFound}
                matchedProducts={preview.matches}
                products={preview.products}
                customFields={companyCustomFields}
                disabled={isSubmitting || isLoadingPreview}
              />
            )}
          </Box>
        </Box>

        {preview && (
          <>
            <Divider sx={{ borderColor: APP_CONFIG.ui.theme.colors.border }} />
            <Box sx={{ px: 4, py: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleConnect}
                disabled={isSubmitting || isLoadingPreview || !canConnect}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isSubmitting ? 'Connecting…' : 'Connect'}
              </Button>
            </Box>
          </>
        )}
      </DialogContainer>
    </Box>
  );
};

export default ShopifyConnectionSetup;
